import {
  CSSProperties,
  ReactNode,
  RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import styles from "./Popover.module.css";

export type PopoverPlacement =
  | "bottom-start"
  | "bottom-end"
  | "top-start"
  | "top-end";

interface Props {
  open: boolean;
  onClose: () => void;
  anchorRef: RefObject<HTMLElement | null>;
  /** Placement relative to anchor. Default 'bottom-start'. */
  placement?: PopoverPlacement;
  /** Offset in pixels from the anchor edge. Default 6. */
  offset?: number;
  children: ReactNode;
  /** Optional max-width / width override */
  width?: number | string;
  /** Optional className passed through */
  className?: string;
}

interface Position {
  top: number;
  left: number;
}

function computePosition(
  anchorRect: DOMRect,
  panelRect: { width: number; height: number },
  placement: PopoverPlacement,
  offset: number,
): Position {
  switch (placement) {
    case "bottom-start":
      return {
        top: anchorRect.bottom + offset,
        left: anchorRect.left,
      };
    case "bottom-end":
      return {
        top: anchorRect.bottom + offset,
        left: anchorRect.right - panelRect.width,
      };
    case "top-start":
      return {
        top: anchorRect.top - offset - panelRect.height,
        left: anchorRect.left,
      };
    case "top-end":
      return {
        top: anchorRect.top - offset - panelRect.height,
        left: anchorRect.right - panelRect.width,
      };
  }
}

export function Popover({
  open,
  onClose,
  anchorRef,
  placement = "bottom-start",
  offset = 6,
  children,
  width,
  className,
}: Props) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<Position | null>(null);

  // Position calculation (after layout, when open).
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    function recompute() {
      const anchor = anchorRef.current;
      const panel = panelRef.current;
      if (!anchor || !panel) return;
      const aRect = anchor.getBoundingClientRect();
      const pRect = {
        width: panel.offsetWidth,
        height: panel.offsetHeight,
      };
      setPos(computePosition(aRect, pRect, placement, offset));
    }
    recompute();
    window.addEventListener("resize", recompute);
    window.addEventListener("scroll", recompute, true);
    return () => {
      window.removeEventListener("resize", recompute);
      window.removeEventListener("scroll", recompute, true);
    };
  }, [open, placement, offset, anchorRef]);

  // Outside click + Escape.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const target = e.target as Node | null;
      if (!target) return;
      const panel = panelRef.current;
      const anchor = anchorRef.current;
      if (panel && panel.contains(target)) return;
      if (anchor && anchor.contains(target)) return;
      onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onClose();
      }
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  const style: CSSProperties = {
    position: "fixed",
    top: pos?.top ?? -9999,
    left: pos?.left ?? -9999,
    // Hide until measured to avoid flash at (0,0).
    visibility: pos ? "visible" : "hidden",
  };
  if (width !== undefined) {
    style.width = typeof width === "number" ? `${width}px` : width;
  }

  return createPortal(
    <div
      ref={panelRef}
      className={`${styles.panel}${className ? ` ${className}` : ""}`}
      style={style}
      role="presentation"
    >
      {children}
    </div>,
    document.body,
  );
}
